import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { differenceInCalendarDays, getDaysInMonth, getMonth, getYear, isSameDay, setDate } from 'date-fns'

import { isDateOutsideRange, isDateOutsideRestrictedList } from '@Utils/datepicker'

import { type Mode, type SelectionType, type Target } from '../../DatePicker.types'
import Day from '../day/Day'

import * as styles from './styles'

const getMonthDays = (month: Date): Date[] =>
  [...Array(getDaysInMonth(month)).keys()].map((day) => setDate(month, day + 1))

const getSelectionType = (
  startDate: Date | undefined,
  isStartDate: boolean,
  isEndDate: boolean,
  endOfRange: Date | undefined,
  isSingleStartDateMode = false,
): SelectionType => {
  if (isStartDate && isEndDate) {
    return 'PLAIN_WITH_CIRCLE'
  }

  const isShowingARange = !!endOfRange && !!startDate && differenceInCalendarDays(endOfRange, startDate) > 0

  if (isEndDate || ((!isShowingARange || isSingleStartDateMode) && isStartDate)) {
    return 'PLAIN'
  }

  if (isShowingARange && isStartDate) {
    return 'CIRCLE'
  }

  return 'NONE'
}

export type MonthProps = {
  currentTarget: Target
  endDate?: Date
  hoveredDate?: Date
  maxDate?: Date
  mode: Mode
  month: Date
  onDaySelect: (clickedDay: Date) => void
  restrictedDatesList?: Date[]
  isDayFocusable?: boolean
  setHoveredDate: (day?: Date | undefined) => void
  startDate?: Date
}

const Month: FC<MonthProps> = ({
  currentTarget,
  endDate,
  hoveredDate,
  maxDate: maxDateProps,
  mode,
  month,
  onDaySelect,
  restrictedDatesList,
  isDayFocusable = false,
  setHoveredDate,
  startDate,
}) => {
  const theme = useTheme()
  const today = new Date()
  const minDate =
    !!startDate && (mode === 'INWARD' || mode === 'SINGLE_END_DATE') && differenceInCalendarDays(today, startDate) < 0
      ? startDate
      : today
  const maxDate = maxDateProps ?? (mode === 'OUTWARD' ? endDate : undefined)

  const orderedWeekDays = [1, 2, 3, 4, 5, 6, 0]

  return (
    <div css={styles.root}>
      <Typography css={styles.title} variant="h3">
        <FormattedMessage id="datepicker_monthNames" values={{ index: `m${getMonth(month)}` }} /> {getYear(month)}
      </Typography>
      <div css={styles.grid(theme)}>
        {orderedWeekDays.map((dayIndex) => (
          <div key={dayIndex} css={styles.dayTitle}>
            <Typography component="span" variant="body2">
              <FormattedMessage id="datepicker_shortDayNames" values={{ index: `d${dayIndex}` }} />
            </Typography>
          </div>
        ))}

        {getMonthDays(month).map((day: Date) => {
          const isStartDate = !!startDate && isSameDay(day, startDate)
          const isEndDate = !!endDate && isSameDay(day, endDate)

          const isSelectable =
            !isDateOutsideRestrictedList(day, restrictedDatesList) && !isDateOutsideRange(day, minDate, maxDate)

          const isHoveredDate = !!hoveredDate && isSelectable && isSameDay(day, hoveredDate)
          const endOfRange = endDate || (currentTarget !== 'INIT' ? hoveredDate : undefined)
          const isSingleStartDateMode = mode === 'SINGLE_START_DATE' || mode === 'SINGLE_START_DATE_WITH_HOURS'
          const selectionType = getSelectionType(startDate, isStartDate, isEndDate, endOfRange, isSingleStartDateMode)

          const isHovered = isHoveredDate && selectionType === 'NONE'

          const isInRange =
            mode !== 'SINGLE_START_DATE' &&
            mode !== 'SINGLE_START_DATE_WITH_HOURS' &&
            !!startDate &&
            !!endOfRange &&
            differenceInCalendarDays(day, startDate) >= 0 &&
            differenceInCalendarDays(day, endOfRange) <= 0 &&
            !(isStartDate && isSameDay(day, endOfRange))

          const isRangeStart = isInRange && isStartDate
          const isRangeEnd = isInRange && isSameDay(day, endOfRange)

          return (
            <Day
              key={day.toUTCString()}
              onDaySelect={onDaySelect}
              selectionType={selectionType}
              isSelectable={isSelectable}
              isInRange={isInRange}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              isHovered={isHovered}
              isFocusable={isDayFocusable}
              setHoveredDate={setHoveredDate}
              day={day}
            />
          )
        })}
      </div>
    </div>
  )
}

export default Month
